import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Button from "../Button";
import { cn } from "@core/utils/cn";
import { createPortal } from "react-dom";
import { assets, icons } from "@core/assets";

export type ActionBarMode = "view" | "edit";

type ActionBarProps = {
  title: string;
  children: ReactNode;
  onClose?: () => void;
  direction?: "left" | "right";
  minWidth?: string;
  className?: string;

  mode?: ActionBarMode;
  onModeChange?: (next: ActionBarMode) => void;
  editable?: boolean;

  saveAs?: "submit" | "button";
  formId?: string;
  onSave?: () => void | Promise<void>;
  saveDisabled?: boolean;
  saveLoading?: boolean;
  onDelete?: () => void;

  extraActions?: ReactNode;
};

const ActionBar = forwardRef<HTMLDivElement, ActionBarProps>(function ActionBar(
  {
    title,
    children,
    onClose,
    direction = "right",
    minWidth = "20rem",
    className = "",
    mode: modeProp,
    onModeChange,
    editable = false,
    saveAs = "button",
    formId,
    onSave,
    saveDisabled,
    saveLoading,
    onDelete,
    extraActions,
  },
  ref,
) {
  const panelRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => panelRef.current!);

  const isControlled = typeof onModeChange === "function";
  const [internalMode, setInternalMode] = useState<ActionBarMode>(
    modeProp ?? "view",
  );

  useEffect(() => {
    if (!isControlled && modeProp) setInternalMode(modeProp);
  }, [modeProp, isControlled]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const mode = isControlled ? (modeProp ?? "view") : internalMode;
  const setMode = (next: ActionBarMode) => {
    if (isControlled) onModeChange?.(next);
    else setInternalMode(next);
  };
  const toggleMode = () => setMode(mode === "view" ? "edit" : "view");

  const sideStyle = useMemo(
    () => (direction === "left" ? { left: 0 } : { right: 0 }),
    [direction],
  );

  const saveBtnProps =
    saveAs === "submit"
      ? { type: "submit" as const, form: formId, onClick: undefined }
      : { type: "button" as const, onClick: onSave };

  const isEdit = mode === "edit";

  return createPortal(
    <div className="fixed inset-0 z-[80]" style={{ background: "var(--color-overlay)" }} onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-labelledby="actionbar-title"
        aria-modal={false}
        onClick={(e) => e.stopPropagation()}
        style={{ ...sideStyle, minWidth }}
        className={cn(
          "absolute top-0 h-screen",
          "w-full md:w-[26rem] lg:w-[33rem]",
          "border border-neutral-200 bg-white",
          "slide--far-left-animation",
          "flex flex-col z-[90]",
          className,
        )}
      >
        {/* Header */}
        <div
          className={cn(
            "flex items-center justify-between shrink-0",
            "h-[59px] min-h-[59px] px-4",
            "border-b border-neutral-200",
          )}
        >
          <h5
            id="actionbar-title"
            className="text-sm font-medium text-secondary-900 capitalize"
          >
            {title}
          </h5>

          <div className={cn("flex items-center", isEdit ? "gap-3" : "gap-1")}>
            {/* Edit mode actions */}
            {isEdit && (
              <>
                <Button
                  variant="solid"
                  size="sm"
                  disabled={saveDisabled || saveLoading}
                  isLoading={saveLoading}
                  {...saveBtnProps}
                  iconOnly
                  leftIcon={assets["save"]}
                  iconStrokeWidth={1.6}
                />
                {onDelete && (
                  <Button
                    variant="text"
                    iconOnly
                    size="sm"
                    leftIcon={icons["trash-2"]}
                    className="text-accent-red-600 hover:text-accent-red-700"
                    onClick={onDelete}
                    aria-label="Delete"
                    iconStrokeWidth={1.7}
                  />
                )}
              </>
            )}

            {extraActions}

            {/* Edit / View toggle */}
            {editable && (
              <Button
                variant="text"
                iconOnly
                size="sm"
                type="button"
                leftIcon={isEdit ? icons["eye"] : icons["square-pen"]}
                iconStrokeWidth={1.7}
                className="text-neutral-400 hover:text-secondary-900"
                aria-label={
                  isEdit ? "Switch to view mode" : "Switch to edit mode"
                }
                onClick={toggleMode}
              />
            )}

            {/* Close */}
            <Button
              variant="text"
              iconOnly
              size="sm"
              type="button"
              leftIcon={icons["x-close"]}
              className="text-neutral-400 hover:text-secondary-900"
              onClick={onClose}
              iconStrokeWidth={1.7}
              aria-label="Close panel"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 py-4 overflow-y-auto overflow-x-hidden">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
});

export default ActionBar;
